import { useOutletContext } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { format } from 'date-fns'
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material'

import { queries } from '../../graphql'
import { Amount, LinearProgress } from '../../components'
import { formatDafTransferDescription } from '../../utils'

export function ViewFundActivity() {
  const { fundId } = useOutletContext()

  const { data, loading, error } = useQuery(queries.funds.myFundActivity, {
    variables: { fundId },
    skip: !fundId,
  })

  const fund = data?.me?.fund

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return <Typography color="error">Error loading activity: {error.message}</Typography>
  }

  // Combine and sort all activities by date
  const activities = []

  // Add contributions
  fund?.contributions?.forEach((c) => {
    activities.push({
      id: `contribution-${c.id}`,
      type: 'Contribution',
      description: c.description || `${c.paymentType} contribution`,
      amount: c.netAmount || c.amount,
      date: new Date(c.createdAt),
      state: c.state,
      isPositive: true,
    })
  })

  // Add grant requests (gifts)
  fund?.grantRequests?.forEach((g) => {
    activities.push({
      id: `grant-${g.id}`,
      type: 'Gift',
      description: g.charity?.accountName || g.description || 'Gift to charity',
      amount: g.netAmount || g.amount,
      date: new Date(g.createdAt),
      state: g.state,
      isPositive: false,
    })
  })

  // Add DAF transfers
  fund?.dafTransfers?.forEach((d) => {
    const isIncoming = d.receivingFundId === fundId
    activities.push({
      id: `daf-${d.id}`,
      type: isIncoming ? 'Transfer In' : 'Transfer Out',
      description: d.description || formatDafTransferDescription(d, { type: isIncoming ? 'received' : 'initiated' }),
      amount: d.netAmount || d.amount,
      date: new Date(d.createdAt),
      state: d.state,
      isPositive: isIncoming,
    })
  })

  // Add balance adjustments
  fund?.balanceAdjustments?.forEach((b) => {
    activities.push({
      id: `adjustment-${b.id}`,
      type: 'Adjustment',
      description: b.description || `${b.walletType} adjustment`,
      amount: Math.abs(b.amount),
      date: new Date(b.createdAt),
      state: 'completed',
      isPositive: b.amount > 0,
    })
  })

  // Sort by date descending
  activities.sort((a, b) => b.date - a.date)

  // Take only last 50 for performance
  const recentActivities = activities.slice(0, 50)

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Recent Activity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing the most recent {recentActivities.length} transactions
      </Typography>

      {recentActivities.length === 0 ? (
        <Typography color="text.secondary">No activity to display</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentActivities.map((activity) => (
                <TableRow key={activity.id} hover>
                  <TableCell>{format(activity.date, 'MMM d, yyyy')}</TableCell>
                  <TableCell>
                    <Chip label={activity.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{activity.description}</TableCell>
                  <TableCell>
                    <StateChip state={activity.state} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      component="span"
                      sx={{
                        color: activity.isPositive ? 'success.main' : 'text.primary',
                        fontWeight: 500,
                      }}
                    >
                      {activity.isPositive ? '+' : '-'}
                      <Amount value={activity.amount} />
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

function StateChip({ state }) {
  const getColor = () => {
    switch (state?.toLowerCase()) {
      case 'completed':
      case 'confirmed':
      case 'paid':
      case 'received':
        return 'success'
      case 'pending':
      case 'scheduled':
      case 'issued':
        return 'warning'
      case 'failed':
      case 'canceled':
      case 'cancelled':
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  return <Chip label={state || 'Unknown'} size="small" color={getColor()} sx={{ textTransform: 'capitalize' }} />
}
